import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import {
  CLINIC_INFORMATION_CLINIC_ID_NOT_EXISTS_API_MESSAGE,
  isClinicInformationClinicIdNotExistsApiError,
} from '@/pages/admin/site-admin-clinic-info/applyClinicInformationApiErrors';
import { isAxiosError } from 'axios';
import { flattenClinicCreateData } from '@/pages/admin/site-admin-clinic-info/clinicFormSchema';
import { apiClient } from '@/shared/lib/axios';
import { getApiErrorMessage, isNotFoundError } from '@/utils/apiError';

type FlattenedClinicData = Omit<ReturnType<typeof flattenClinicCreateData>, 'clinic_id'> & {
  clinic_id: string | null;
};

const createClinicInfo = async (siteType: string, data: FlattenedClinicData) => {
  const response = await apiClient.post(`/admin/${siteType}/clinic-informations`, data);
  return response.data;
};

export const useAdminClinicInfoCreate = () => {
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: ({ siteType, data }: { siteType: string; data: FlattenedClinicData }) =>
      createClinicInfo(siteType, data),
    meta: { suppressNotFoundToast: true },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['clinic-informations', variables.siteType],
      });
      toast.success('更新しました。');
    },
    onError: error => {
      if (isClinicInformationClinicIdNotExistsApiError(error)) {
        toast.error(CLINIC_INFORMATION_CLINIC_ID_NOT_EXISTS_API_MESSAGE);
        return;
      }
      // 404 from service findOrFail (e.g. clinic missing / scope); avoid global "データが見つかりません。"
      if (isNotFoundError(error)) {
        toast.error(CLINIC_INFORMATION_CLINIC_ID_NOT_EXISTS_API_MESSAGE);
        return;
      }
      // 422 validation errors: show generic toast, field errors are mapped by applyClinicInformationApiValidationErrors
      if (isAxiosError(error) && error.response?.status === 422) {
        toast.error('エラーが発生しました。');
        return;
      }
      const msg = getApiErrorMessage(error, 'エラーが発生しました。');
      toast.error(msg);
    },
  });
};
